import {Injectable} from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ODataQueryBuilderService {
    private selects: { [context: string]: string[] } = { root: [] };
    private filters: { [context: string]: string[] } = {root: []};
    private orderBy: { [context: string]: string[] } = {root: []};
    private skip?: number;
    private top?: number;
    private currentContext: string = "root";

    public addFilter(filter: string): ODataQueryBuilderService {
        this.filters[this.currentContext].push(filter);
        return this;
    }

    public addFilterForCollectionWithNestedObjects(collectionName: string, tagName: string, conditions: string[]) {
        this.filters[this.currentContext].push(`${collectionName}/any(${tagName}: ${conditions.map(condition => tagName + "/" + condition).join(" and ")})`);
        return this;
    }

    public addFilterForCollection(collectionName: string, tagName: string, conditions: string[]) {
        this.filters[this.currentContext].push(`${collectionName}/any(${tagName}: ${conditions.join(" and ")})`);
        return this;
    }

    public addEmptyCollectionFilter(collectionName: string, allowEmpty: boolean = true) {
        this.filters[this.currentContext].push((allowEmpty ? "not " : "") + `${collectionName}/any()`);
        return this;
    }

    public addOrderBy(property: string, direction: 'asc' | 'desc' = 'asc'): ODataQueryBuilderService {
        this.orderBy[this.currentContext].push(`${property} ${direction}`);
        return this;
    }

    public addSelects(fields: string[]): ODataQueryBuilderService {
        this.selects[this.currentContext].push(...fields);
        return this;
    }

    public addExpand(property: string): ODataQueryBuilderService {
        this.filters[property] = this.filters[property] ?? [];
        this.orderBy[property] = this.orderBy[property] ?? [];
        return this;
    }

    public setSkip(skip: number): ODataQueryBuilderService {
        this.skip = skip;
        return this;
    }

    public setTop(top: number): ODataQueryBuilderService {
        this.top = top;
        return this;
    }

    public setContext(context: string): ODataQueryBuilderService {
        this.currentContext = context;
        return this;
    }

    public resetContext(): ODataQueryBuilderService {
        this.currentContext = "root";
        return this;
    }

    public build(): string {
        const parts: string[] = [];
        parts.push(...this.buildContextQueryParts("root"));

        if (this.skip) {
            parts.push(`$skip=${this.skip}`);
        }

        if (this.top) {
            parts.push(`$top=${this.top}`);
        }

        const expandParts = Object.keys(this.filters).filter(context => context !== "root").map(context => {
            const subParts: string[] = this.buildContextQueryParts(context);
            return `${context}${subParts.length > 0 ? ("(" + subParts.join(";") + ")") : ""}`;
        });

        if (expandParts.length > 0) {
            parts.push(`$expand=${expandParts.join(',')}`);
        }

        this.clearData();
        return "?" + parts.join('&');
    }

    private buildContextQueryParts(context: string): string[] {
        const parts: string[] = [];
        if (this.selects[context]?.length > 0) {
            parts.push(`$select=${this.selects[context].join(',')}`);
        }

        if (this.filters[context]?.length > 0) {
            parts.push(`$filter=${this.filters[context].join(" and ")}`);
        }

        if (this.orderBy[context]?.length > 0) {
            parts.push(`$orderby=${this.orderBy[context].join(", ")}`);
        }
        return parts;
    }

    private clearData() {
        this.filters = {root: []};
        this.orderBy = {root: []};
        this.selects = { root: [] };
        this.skip = undefined;
        this.top = undefined;
        this.currentContext = "root";
    }
}
